import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react'
import { playNotificationSound } from 'lib/notificationUtils'
import {
  Notification,
  PusherNotificationEvent,
  NotificationEventType,
} from 'types/notification'
import { useTranslation } from 'react-i18next'
import { usePusher } from 'hooks/usePusher'
import { useAuth } from './AuthProvider'
import { useAlert } from './AlertProvider'

interface NotificationContextType {
  notifications: Notification[]
  clearNotification: (id: string) => void
  clearAllNotifications: () => void
  isOrderFromNotification: (orderId: string) => boolean
  getOrderNotificationType: (orderId: string) => NotificationEventType | null
  notificationsEnabled: boolean
  toggleNotifications: () => void
  openNotificationModal: () => void
  notificationModalOpen: boolean
  closeNotificationModal: () => void
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  clearNotification: () => {
    // Default implementation
  },
  clearAllNotifications: () => {
    // Default implementation
  },
  isOrderFromNotification: () => false,
  getOrderNotificationType: () => null,
  notificationsEnabled: true,
  toggleNotifications: () => {
    // Default implementation
  },
  openNotificationModal: () => {
    // Default implementation
  },
  notificationModalOpen: false,
  closeNotificationModal: () => {
    // Default implementation
  },
})

export const useNotifications = () => useContext(NotificationContext)

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider = ({
  children,
}: NotificationProviderProps) => {
  const { userStores } = useAuth()
  const { setAlert } = useAlert()
  const { t } = useTranslation()

  // Notification enabled state - persisted in localStorage
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(
    () => {
      const stored = localStorage.getItem('notificationsEnabled')
      return stored === null ? true : stored === 'true'
    }
  )

  // Notification modal state
  const [notificationModalOpen, setNotificationModalOpen] = useState(false)

  // Initialize with notifications from localStorage
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    const stored = localStorage.getItem('notifications')
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch {
        return []
      }
    }
    return []
  })

  // Persist notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications))
  }, [notifications])

  const storeIds = userStores && userStores.length > 0 ? userStores : []

  const handleNewNotification = useCallback(
    (event: PusherNotificationEvent) => {
      // Don't add notifications if they're disabled
      if (!notificationsEnabled) {
        return
      }

      const newNotification: Notification = {
        id: `${event.order_id}-${Date.now()}`,
        event: event.event,
        parentOrderId: event.parent_order_id,
        orderId: event.order_id,
        placeId: event.place_id,
        visibleId: event.visible_id,
        timestamp: Date.now(),
      }

      setNotifications((prev) => [newNotification, ...prev])

      const isError = event.event !== 'order_created'
      playNotificationSound(isError ? 'error' : 'notification')

      // Show toast notification at the bottom of the screen
      const message = t(`notification_${event.event}`, {
        order_id: event.visible_id,
      })
      setAlert(
        message,
        'notification',
        {
          vertical: 'bottom',
          horizontal: 'center',
        },
        () => setNotificationModalOpen(true)
      )
    },
    [notificationsEnabled, setAlert, t]
  )

  // Initialize Pusher connection
  usePusher(storeIds, handleNewNotification)

  const clearNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id))
  }, [])

  const clearAllNotifications = useCallback(() => {
    setNotifications([])
  }, [])

  const isOrderFromNotification = useCallback(
    (orderId: string) => {
      return notifications.some((n) => n.parentOrderId.toString() === orderId)
    },
    [notifications]
  )

  const getOrderNotificationType = useCallback(
    (orderId: string): NotificationEventType | null => {
      const notification = notifications.find(
        (n) => n.parentOrderId.toString() === orderId.toString()
      )
      return notification ? notification.event : null
    },
    [notifications]
  )

  const toggleNotifications = useCallback(() => {
    setNotificationsEnabled((prev) => {
      const newValue = !prev
      localStorage.setItem('notificationsEnabled', String(newValue))
      return newValue
    })
  }, [])

  const openNotificationModal = useCallback(() => {
    setNotificationModalOpen(true)
  }, [])

  const closeNotificationModal = useCallback(() => {
    setNotificationModalOpen(false)
  }, [])

  const value = useMemo(
    () => ({
      notifications,
      clearNotification,
      clearAllNotifications,
      isOrderFromNotification,
      getOrderNotificationType,
      notificationsEnabled,
      toggleNotifications,
      openNotificationModal,
      notificationModalOpen,
      closeNotificationModal,
    }),
    [
      notifications,
      clearNotification,
      clearAllNotifications,
      isOrderFromNotification,
      getOrderNotificationType,
      notificationsEnabled,
      toggleNotifications,
      openNotificationModal,
      notificationModalOpen,
      closeNotificationModal,
    ]
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}
